import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  inject,
  signal,
  computed,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { RH_ACTIONS } from '@shared/constants/rh-actions.constants';
import { VacacionesService } from '@services/vacaciones.service';
import {
  SolicitudVacaciones,
  SaldoVacaciones,
  EstatusVacaciones,
} from '@schemas/vacaciones.schema';
import {
  getEstatusColor as _getEstatusColor,
  getResultadoTexto as _getResultadoTexto,
  getResultadoTipo as _getResultadoTipo,
  getResultadoMotivo as _getResultadoMotivo,
} from '@shared/vacaciones.helpers';
import { SolicitudDetalleModal } from '../../components/solicitud-detalle-modal/solicitud-detalle-modal';
import { SolicitudAprobarModal } from '../../components/solicitud-aprobar-modal/solicitud-aprobar-modal';

import {
  CellStack,
  EmptyState,
  PageHeader,
  TabLabel,
  TableActions,
  type ActionButtonDef,
  DataTable,
  CellTemplateDirective,
  type ColumnDef,
  type TableAction,
} from '@intelishs-group/ui';

import { NzTooltipModule } from 'ng-zorro-antd/tooltip';

@Component({
  selector: 'app-autorizaciones-rh',
  imports: [
    TabLabel,
    CommonModule,
    FormsModule,
    NzIconModule,
    NzButtonModule,
    NzModalModule,
    NzTabsModule,
    NzBadgeModule,
    NzSpinModule,
    NzDividerModule,
    NzAlertModule,
    DataTable,
    CellTemplateDirective,
    TableActions,
    CellStack,
    NzTooltipModule,
    PageHeader,
    EmptyState,
    SolicitudDetalleModal,
    SolicitudAprobarModal,
  ],
  templateUrl: './autorizaciones-rh.page.html',
  styleUrl: './autorizaciones-rh.page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutorizacionesRhPage implements OnInit {
  private readonly vacacionesService = inject(VacacionesService);
  private readonly destroyRef = inject(DestroyRef);

  pendientesDirector = signal<SolicitudVacaciones[]>([]);
  pendientesRH = signal<SolicitudVacaciones[]>([]);
  loading = signal(false);

  /**
   * Lo que espera la decisión de RH: lo que ya aprobó el jefe y, detrás, lo
   * que todavía no. RH tiene la última palabra sin importar la jerarquía, así
   * que puede resolver una Pendiente sin esperar al jefe; la API lo acepta y
   * deja vacía la aprobación del jefe.
   *
   * Se arma aquí con las dos listas que la página ya descarga, y no cambiando
   * `GET /pendientes/rh`: el dashboard cuenta con ese endpoint "lo que ya pasó
   * por el jefe", y ese número no tiene por qué moverse.
   */
  porResolverRH = computed(() => [...this.pendientesRH(), ...this.pendientesDirector()]);

  totalDirector = computed(() => this.pendientesDirector().length);
  totalRH = computed(() => this.porResolverRH().length);

  // ── Columnas Tab RH ──
  /**
   * Sin columna de estado: el estatus es la pestaña. Solo se anclan las
   * acciones, que es lo que queda inalcanzable al scrollear.
   *
   * Antes eran 9 columnas (1160px). El paso previo del flujo —quién aprobó y
   * qué comentó— eran dos columnas separadas y se leen mejor juntas.
   */
  columnsRH: ColumnDef<SolicitudVacaciones>[] = [
    {
      key: 'empleadoNombre',
      header: 'Empleado',
      // `'auto'` explicito y no vacio: hoy la tabla es fluida y vacio funciona,
      // pero el dia que alguien ancle una columna ng-zorro mide y CONGELA en
      // pixeles todas las que no declaren ancho, y no las recalcula nunca.
      width: 'auto',
      // La celda es un `cell-stack` dentro de `.celda-empleado`: las dos lineas
      // no truncan solas, y sin el flag el nombre largo ensancha la columna.
      // El recorte llega al `cell-stack` porque el wrapper flex declara
      // `min-width: 0` (ver el CSS de esta pagina).
      ellipsis: true,
      filterable: true,
      sortable: true,
      type: 'custom',
    },
    {
      key: 'fechaInicio',
      header: 'Periodo',
      width: '200px',
      type: 'custom',
      ellipsis: true,
      sortable: true,
    },
    {
      key: 'aprobadoPorDirector',
      // "Jefe" y no "Aprobó Director": en una Pendiente el campo trae al jefe
      // ASIGNADO (se pre-asigna al crear), que todavía no ha aprobado.
      header: 'Jefe',
      width: 'auto',
      type: 'custom',
      // El comentario del director es texto libre en la linea meta: es
      // justamente lo que ensancha la columna sin el flag.
      ellipsis: true,
      filterable: true,
    },
    // 3 botones de 24px + 2 gaps = 84px más el padding de celda.
    {
      key: 'acciones',
      header: 'Acciones',
      width: '100px',
      align: 'center',
      type: 'custom',
    },
  ];

  /** Campos que pasaron a la línea meta de otra celda. */
  readonly extraFilterKeys = ['empleadoIdInterno', 'area', 'comentariosDirector'];

  /** Tab Director: ID y área viven en la línea meta del empleado. */
  readonly extraFilterKeysDir = ['empleadoIdInterno', 'area', 'comentarios'];

  /** El Excel conserva el desglose que la tabla unificó. */
  readonly exportColumns: ColumnDef<SolicitudVacaciones>[] = [
    { key: 'empleadoIdInterno', header: 'ID Interno' },
    { key: 'empleadoNombre', header: 'Empleado' },
    { key: 'area', header: 'Área' },
    { key: 'fechaInicio', header: 'Fecha Inicio', type: 'date' },
    { key: 'fechaFin', header: 'Fecha Fin', type: 'date' },
    { key: 'diasSolicitados', header: 'Días' },
    { key: 'estatus', header: 'Estatus' },
    { key: 'aprobadoPorDirector', header: 'Jefe' },
    { key: 'comentariosDirector', header: 'Comentarios Director' },
  ];

  /**
   * Tab Director: solo lectura, así que no hay columna de acciones ni nada que
   * anclar. De 9 columnas (1240px) a 5.
   *
   * El director asignado se queda en columna propia: en esta pestaña es el dato
   * accionable —a quién hay que empujar para que resuelva—.
   */
  columnsDir: ColumnDef<SolicitudVacaciones>[] = [
    {
      key: 'empleadoNombre',
      header: 'Empleado',
      width: 'auto',
      type: 'custom',
      ellipsis: true,
      filterable: true,
      sortable: true,
    },
    {
      key: 'aprobadoPorDirector',
      header: 'Director Asignado',
      width: 'auto',
      ellipsis: true,
      filterable: true,
    },
    {
      key: 'fechaInicio',
      header: 'Periodo',
      width: '200px',
      type: 'custom',
      ellipsis: true,
      sortable: true,
    },
    {
      key: 'fechaSolicitud',
      header: 'Solicitada',
      // Medido, no estimado: el td va a 14px con tabular-nums (dígitos de ancho
      // fijo, ~8.4px) y 10px de padding por lado, así que "17/08/2026 14:30"
      // ocupa ~138px. Con los 140px anteriores quedaban 2px de margen y hacía
      // wrap; en table-layout: fixed la columna no se expande, parte el texto.
      //
      // En UNA línea a propósito: fecha y hora son un mismo dato temporal, no
      // dato + contexto, y esta tabla ya tiene dos columnas apiladas —si todo
      // se apila, el recurso deja de comunicar jerarquía—.
      width: '160px',
      align: 'center',
      sortable: true,
      type: 'date',
      // Sin segundos: la hora exacta no aporta al escanear la lista.
      dateFormat: 'dd/MM/yyyy HH:mm',
    },
    // El comentario del empleado era una columna de 200px con texto libre de
    // largo impredecible. Se lee en el modal de detalle, que además lo muestra
    // en el flujo (quién comentó y cuándo) en vez de suelto.
    {
      key: 'acciones',
      header: 'Acciones',
      width: '70px',
      align: 'center',
      type: 'custom',
    },
  ];

  /** Un solo botón: esta pestaña es de consulta, la resuelve el director. */
  readonly dirActions: ActionButtonDef[] = [RH_ACTIONS.ver];

  // ── Acciones Tab RH ──
  readonly rhActions: ActionButtonDef[] = [RH_ACTIONS.ver, RH_ACTIONS.aprobar, RH_ACTIONS.rechazar];

  onActionRH(event: TableAction<SolicitudVacaciones>): void {
    switch (event.action) {
      case 'ver':
        this.abrirDetalle(event.row);
        break;
      case 'aprobar':
        this.abrirModal(event.row, 'aprobar', 'rh');
        break;
      case 'rechazar':
        this.abrirModal(event.row, 'rechazar', 'rh');
        break;
    }
  }

  // Modal aprobación — el modal carga el saldo internamente
  modalVisible = signal(false);
  modalAccion = signal<'aprobar' | 'rechazar'>('aprobar');
  modalPaso = signal<'director' | 'rh'>('director');
  solicitudSeleccionada = signal<SolicitudVacaciones | null>(null);

  // Cache de saldos por empleadoId para mostrar badges en la tabla
  saldosCache = signal<Map<number, SaldoVacaciones>>(new Map());

  // Modal detalle de solicitud — el modal carga el saldo internamente
  detalleVisible = signal(false);
  detalleEmpleado = signal<SolicitudVacaciones | null>(null);

  ngOnInit(): void {
    this.cargar();
  }

  abrirDetalle(sol: SolicitudVacaciones): void {
    this.detalleEmpleado.set(sol);
    this.detalleVisible.set(true);
  }

  getEstatusColor = _getEstatusColor;

  getResultadoTexto = _getResultadoTexto;
  getResultadoTipo = _getResultadoTipo;
  getResultadoMotivo = _getResultadoMotivo;

  cargar(): void {
    this.loading.set(true);
    this.vacacionesService
      .getPendientesDirector()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.pendientesDirector.set(data);
          this.precargarSaldosRH(data);
        },
      });
    this.vacacionesService
      .getPendientesRH()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.pendientesRH.set(data);
          this.loading.set(false);
          this.precargarSaldosRH(data);
        },
        error: () => this.loading.set(false),
      });
  }

  /** Precarga saldos de empleados únicos en pendientesRH para mostrar badges en tabla. */
  private precargarSaldosRH(solicitudes: SolicitudVacaciones[]): void {
    const idsUnicos = [...new Set(solicitudes.map((s) => s.empleadoId))];
    idsUnicos.forEach((id) => {
      if (this.saldosCache().has(id)) return;
      this.vacacionesService
        .getSaldo(id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (s) => {
            const map = new Map(this.saldosCache());
            map.set(id, s);
            this.saldosCache.set(map);
          },
        });
    });
  }

  /**
   * Indica si un empleado tiene deuda real según el cache. No `total < 0`: el
   * total es a hoy, y una solicitud a futuro que se cubre a su fecha lo deja
   * negativo sin que falte nada.
   */
  empleadoTieneSaldoNegativo(empleadoId: number): boolean {
    return this.saldosCache().get(empleadoId)?.tieneSaldoNegativo ?? false;
  }

  abrirModal(
    solicitud: SolicitudVacaciones,
    accion: 'aprobar' | 'rechazar',
    paso: 'director' | 'rh',
  ): void {
    this.solicitudSeleccionada.set(solicitud);
    this.modalAccion.set(accion);
    this.modalPaso.set(paso);
    this.modalVisible.set(true);
  }

  onAprobacionProcesada(): void {
    this.cargar();
  }
}
